/** tm/legal/LegalTitleEdit.tsx — 법령·판례·해석 «내 제목» 바꾸기 (2026-10-01)
 *  TitleEditButton : 제목 옆 작은 「✎ 제목 수정」
 *  TitleEditor     : 제목 자리에 들어가는 입력칸 — Enter·「저장」 저장, Esc·「취소」 취소, 내 제목이 있으면 「원래 제목으로」
 *  저장 값 규칙(공백 정리 · 비었거나 원래 제목과 같으면 원래 제목으로)은 서버(legalItemRepo.normTitle)와 같다.
 */
import { useEffect, useRef, useState } from 'react';
import { C, T, R } from '../components/ui';
import { btn, inputStyle, MUTED } from './legalTheme';

export const TITLE_MAX = 300;
/** 앞뒤 공백 제거 · 연속 공백 1칸 */
export const squashTitle = (t: string | null | undefined) => String(t || '').replace(/\s+/g, ' ').trim();
/** 저장할 내 제목 — 비었거나 원래 제목과 같으면 null */
export function nextCustomTitle(draft: string, original: string): string | null {
  const t = squashTitle(draft);
  return !t || t === squashTitle(original) ? null : t;
}

export function TitleEditButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="lw-f lw-tedit" onClick={onClick} title="이 제목을 내 제목으로 바꿉니다 (나에게만 보임)"
      style={{ marginLeft: 8, verticalAlign: 'middle', border: `1px solid ${C.border}`, background: C.white, color: C.sub, borderRadius: R.sm, padding: '1px 8px', fontSize: T.sm, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', whiteSpace: 'nowrap', lineHeight: 1.6 }}>
      ✎ 제목 수정
    </button>
  );
}

interface EditorProps {
  original: string; custom: string | null | undefined; mobile: boolean;
  onSave: (next: string | null) => void; onCancel: () => void;
}

export function TitleEditor({ original, custom, mobile, onSave, onCancel }: EditorProps) {
  const [draft, setDraft] = useState(custom || original);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); input.current?.select(); }, []);
  const len = squashTitle(draft).length;
  const over = len > TITLE_MAX;
  const save = () => { if (!over) onSave(nextCustomTitle(draft, original)); };
  return (
    <div style={{ margin: '0 0 10px', maxWidth: '62ch' }}>
      <label htmlFor="lw-title-edit" style={{ display: 'block', fontSize: T.sm, fontWeight: 600, color: C.sub, marginBottom: 4 }}>
        내 제목 <span style={{ fontWeight: 400, color: MUTED }}>— 나에게만 보이고, 뒤에 원래 제목이 함께 표시됩니다</span>
      </label>
      <input id="lw-title-edit" ref={input} className="lw-f" value={draft} maxLength={TITLE_MAX + 200}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.nativeEvent.isComposing) return; // 한글 조합 중 Enter 는 글자 확정
          if (e.key === 'Enter') { e.preventDefault(); save(); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        aria-invalid={over || undefined}
        style={{ ...inputStyle, fontSize: mobile ? 16 : T.lg, /* 16px: iOS 확대 방지 */ fontWeight: 600, padding: '8px 10px', borderColor: over ? C.red : C.accent, boxShadow: `0 0 0 3px ${over ? C.redLt : C.accentLt}` }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        <button type="button" className="lw-f" onClick={save} disabled={over} style={{ ...btn('pri'), opacity: over ? 0.5 : 1, cursor: over ? 'not-allowed' : 'pointer' }}>저장</button>
        <button type="button" className="lw-f" onClick={onCancel} style={btn()}>취소</button>
        {custom && (
          <button type="button" className="lw-f" onClick={() => onSave(null)}
            style={{ border: 0, background: 'transparent', color: C.accent, fontSize: T.sm, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', padding: '6px 4px', textDecoration: 'underline', textUnderlineOffset: 2 }}>
            원래 제목으로
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: T.xs, color: over ? C.red : MUTED, fontVariantNumeric: 'tabular-nums' }}>
          {over ? `${TITLE_MAX}자까지 쓸 수 있습니다 (${len}자)` : len > TITLE_MAX - 50 ? `${len}/${TITLE_MAX}` : ''}
        </span>
      </div>
      <div style={{ marginTop: 6, fontSize: T.sm, color: MUTED, lineHeight: 1.5, wordBreak: 'keep-all' }}>원래 제목: {original}</div>
    </div>
  );
}
